import React from 'react';
import s from '../DietPlanBuilder.module.css';
import { PageHead } from '../BuilderShell';
import { Toggle } from '../ui';
import { copy } from '../../copy';
import { fmtInt, inkFor } from '../../lib/format';
import { mealShareBarLabels, mealTargetsFootMacroProps, mixLabel, mixWidths } from './mealTargets.shared';
import type { MacroKey } from '../../types';
import type { useMealTargetsStep } from '../../hooks/useMealTargetsStep';
import { MealTargetsBanner } from './MealTargetsBanner';
import { MacroFoodIcon } from '../MacroFoodIcon';
import { MACRO_META } from './goalsStep.shared';

const MACRO_BAR: Record<MacroKey, string> = { p: '#444CE7', c: '#E7C144', f: '#2D8282' };

type Model = ReturnType<typeof useMealTargetsStep>;

export function MealTargetsStepDesktop({
  planKcal,
  t,
  onEditGoals,
}: {
  planKcal: number;
  t: Model;
  onEditGoals: () => void;
}) {
  const sharesOk = t.shareSum === 100;

  return (
    <div className={s.mealTargetsPage}>
      <PageHead
        title={copy.targets.title}
        sub={copy.targets.sub}
        action={
          <div className={s.segToggle} role="group" aria-label="Meal share source">
            <button
              type="button"
              className={s.segToggleBtn}
              data-on={t.split === 'template'}
              disabled={!t.canTemplate}
              onClick={t.useTemplate}
            >
              {copy.targets.modeTemplate}
            </button>
            <button
              type="button"
              className={s.segToggleBtn}
              data-on={t.split === 'standard'}
              onClick={t.useStandard}
            >
              {copy.targets.modeStandard}
            </button>
          </div>
        }
      />

      <div className={s.dailyGoalStrip}>
        <span className={s.dailyGoalEyebrow}>{copy.targets.dailyGoal}</span>
        <span className={s.dailyGoalKcal}>
          <b>{fmtInt(planKcal)}</b> <span className={s.dailyGoalUnit}>kcal</span>
        </span>
        <span className={s.dailyGoalMacro}>
          <b>{t.gp} g</b> <span className={s.dailyGoalUnit}>protein</span>
        </span>
        <span className={s.dailyGoalMacro}>
          <b>{t.gc} g</b> <span className={s.dailyGoalUnit}>carbs</span>
        </span>
        <span className={s.dailyGoalMacro}>
          <b>{t.gf} g</b> <span className={s.dailyGoalUnit}>fats</span>
        </span>
        <button type="button" className={s.btnLink} style={{ marginLeft: 'auto' }} onClick={onEditGoals}>
          {copy.targets.editGoals}
        </button>
      </div>

      <div className={s.mealTargetsCard}>
        <div className={s.mealTargetsShareBar} aria-hidden>
          {t.active
            .filter(r => r.m.share > 0)
            .map(({ m }) => {
              const bar = mealShareBarLabels(m.share, t.barDenom, m.name, m.key);
              return (
                <span
                  key={m.key}
                  className={s.mealTargetsShareSeg}
                  style={{
                    width: `${(m.share / t.barDenom) * 100}%`,
                    background: m.color,
                    color: inkFor(m.color),
                  }}
                  title={`${m.name}, ${m.share}% of the day`}
                >
                  {bar.label ? <b>{bar.label}</b> : null}
                  {bar.pct ? <span>{bar.pct}</span> : null}
                </span>
              );
            })}
        </div>

        <div className={s.mealTargetsScroll}>
          <div className={s.mealTargetsGrid}>
            <span className={s.mealTargetsTh}>{copy.targets.colOn}</span>
            <span className={s.mealTargetsTh}>{copy.targets.colMeal}</span>
            <span className={s.mealTargetsTh}>{copy.targets.colStart}</span>
            <span className={s.mealTargetsTh}>{copy.targets.colEnd}</span>
            <span className={s.mealTargetsTh}>{copy.targets.colShare}</span>
            <span className={s.mealTargetsTh}>{copy.targets.colKcal}</span>
            <span className={`${s.mealTargetsTh} ${s.mealTargetsThIcon}`}>
              <span style={{ color: MACRO_META.p.bg, display: 'inline-flex' }}><MacroFoodIcon k="p" size={11} /></span>
              {copy.targets.colProtein}
            </span>
            <span className={`${s.mealTargetsTh} ${s.mealTargetsThIcon}`}>
              <span style={{ color: MACRO_META.c.bg, display: 'inline-flex' }}><MacroFoodIcon k="c" size={11} /></span>
              {copy.targets.colCarbs}
            </span>
            <span className={`${s.mealTargetsTh} ${s.mealTargetsThIcon}`}>
              <span style={{ color: MACRO_META.f.bg, display: 'inline-flex' }}><MacroFoodIcon k="f" size={11} /></span>
              {copy.targets.colFats}
            </span>
            <span className={s.mealTargetsTh}>{copy.targets.colMix}</span>
            <span className={s.mealTargetsTh} />

            {t.rows.map(({ m, i, T, editedMacro, source }) => {
              const mix = mixWidths(T);
              const off = m.skip;
              return (
                <React.Fragment key={m.key}>
                  <Toggle
                    on={!m.skip}
                    label={copy.targets.includeMeal(m.name)}
                    onChange={v => t.setSkip(i, !v)}
                  />
                  <div className={s.mealTargetsMealCell} style={{ opacity: off ? 0.45 : 1 }}>
                    <span className={s.mealTargetsSwatch} style={{ background: m.color }} />
                    <div className={s.mealTargetsMealText}>
                      <span className={s.mealTargetsMealName}>{m.name}</span>
                      <span className={s.mealTargetsMealSub}>{source}</span>
                    </div>
                  </div>
                  <input
                    className={s.mealTargetsInput}
                    type="time"
                    value={m.time}
                    disabled={off}
                    onChange={e => t.setTime(i, e.target.value)}
                    aria-label={`${m.name} start time`}
                  />
                  <input
                    className={s.mealTargetsInput}
                    type="time"
                    value={m.endTime}
                    disabled={off}
                    onChange={e => t.setEndTime(i, e.target.value)}
                    aria-label={`${m.name} end time`}
                  />
                  <div className={s.mealTargetsShareField} style={{ opacity: off ? 0.45 : 1 }}>
                    <input
                      type="number"
                      inputMode="decimal"
                      disabled={off}
                      value={off ? '' : m.share}
                      onChange={e => t.setShare(i, parseFloat(e.target.value) || 0)}
                      aria-label={`${m.name} share`}
                    />
                    <span>%</span>
                  </div>
                  <input
                    className={s.mealTargetsInput}
                    type="number"
                    inputMode="numeric"
                    disabled={off}
                    data-edited={!off && Math.abs(Math.round(T.k) - Math.round(planKcal * m.share / 100)) > 1}
                    value={off ? '' : Math.round(T.k)}
                    onChange={e => t.setKcal(i, parseFloat(e.target.value) || 0)}
                    aria-label={`${m.name} kcal`}
                  />
                  {(['p', 'c', 'f'] as MacroKey[]).map(k => (
                    <input
                      key={k}
                      className={s.mealTargetsInput}
                      type="number"
                      inputMode="decimal"
                      disabled={off}
                      data-edited={editedMacro(k)}
                      value={off ? '' : Math.round(T[k] * 10) / 10}
                      onChange={e => t.setMacro(i, k, parseFloat(e.target.value) || 0)}
                      aria-label={`${m.name} ${k}`}
                    />
                  ))}
                  <div className={s.mealTargetsMix} style={{ opacity: off ? 0.45 : 1 }}>
                    <div className={s.mealTargetsMixBar}>
                      <span style={{ width: mix.p, background: MACRO_BAR.p }} />
                      <span style={{ width: mix.c, background: MACRO_BAR.c }} />
                      <span style={{ width: mix.f, background: MACRO_BAR.f }} />
                    </div>
                    <span className={s.mealTargetsMixLabel}>{off ? '—' : mixLabel(T)}</span>
                  </div>
                  <span />
                </React.Fragment>
              );
            })}

            <span className={s.mealTargetsFootSpacer} />
            <span className={s.mealTargetsFootLabel}>{copy.targets.totalMeals}</span>
            <span className={s.mealTargetsFootSpacer} />
            <span className={s.mealTargetsFootSpacer} />
            <span className={s.mealTargetsFootVal} data-ok={sharesOk}>
              {Math.round(t.totals.share * 10) / 10}%
            </span>
            <span className={s.mealTargetsFootVal} data-ok={sharesOk}>{fmtInt(t.totals.k)}</span>
            <span className={s.mealTargetsFootVal} {...mealTargetsFootMacroProps(sharesOk, t.macroDrift.p)}>
              {Math.round(t.totals.p)}
            </span>
            <span className={s.mealTargetsFootVal} {...mealTargetsFootMacroProps(sharesOk, t.macroDrift.c)}>
              {Math.round(t.totals.c)}
            </span>
            <span className={s.mealTargetsFootVal} {...mealTargetsFootMacroProps(sharesOk, t.macroDrift.f)}>
              {Math.round(t.totals.f)}
            </span>
            <span className={s.mealTargetsFootSpacer} />
            <span />

            <span />
            <span className={s.mealTargetsGoalRow}>{copy.targets.dailyGoal}</span>
            <span />
            <span />
            <span className={s.mealTargetsGoalVal}>100%</span>
            <span className={s.mealTargetsGoalVal}>{fmtInt(planKcal)}</span>
            <span className={s.mealTargetsGoalVal}>{t.gp}</span>
            <span className={s.mealTargetsGoalVal}>{t.gc}</span>
            <span className={s.mealTargetsGoalVal}>{t.gf}</span>
            <span />
            <span />
          </div>
        </div>
      </div>

      <MealTargetsBanner
        shareSum={t.shareSum}
        macroDrifts={t.macroDrifts}
        onScaleShares={t.scaleShares}
      />
    </div>
  );
}
